import {
  AfterViewInit, ApplicationRef, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Inject, Input,
  OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID, TemplateRef, ViewChild
} from '@angular/core';

import { fromEvent, interval, Observable, Subject, Subscription, debounceTime, distinctUntilChanged, takeUntil, take, filter, switchMap } from 'rxjs';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';

import { BaseFormErrorTranslateService } from '../forms/form-common-error/base-form-error-translate.service';
import { DeviceService } from '../../_services/device.service';
import { ErrorTranslateService } from '../forms/error-translate-service';
import { expandCollapseAnimation } from '../../_animations/expand-collapse.animation';
import { IconSizeEnum } from '../../_enums/icon.enum';
import { ISearch } from '../../_interfaces/search.interface';
import { SdButtonComponent } from '../buttons/button/button.component';
import { SdButtonIconComponent } from '../buttons/button-icon/button-icon.component';
import { SdFormInputComponent } from '../forms/form-input/form-input.component';
import { SdLinkComponent } from '../links/link/link.component';
import { SdListComponent } from '../lists/list/list.component';
import { SdNavigationProductCardComponent } from '../navigation-product-card/navigation-product-card.component';
import { SdPushCardComponent } from '../push-card/push-card.component';
import { ThemesEnum } from '../../_enums/themes.enum';
import { SdPreviewArticleComponent } from '../preview-article/preview-article.component';
import { SdHtmlComponent } from '../html/html.component';

@Component({
  selector: 'sd-search',
  templateUrl: './search.component.html',
  animations: [
    expandCollapseAnimation({
      anchor: 'expandCollapse',
      durationIn: 250,
      delayIn: 0,
      durationOut: 250,
      delayOut: 0
    })
  ],
  standalone: true,
  imports: [
    SdFormInputComponent,
    SdButtonIconComponent,
    SdButtonComponent,
    SdListComponent,
    SdLinkComponent,
    NgTemplateOutlet,
    SdPreviewArticleComponent,
    SdNavigationProductCardComponent,
    TranslateModule,
    SdHtmlComponent
  ],
  providers: [
    { provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useValue: { overlayPanelClass: 'is-autocomplete-panel' } },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ]
})
export class SdSearchComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {
  @Input()
  hasFormToggle = true;

  @Input()
  alpha = false;

  @Input()
  themesConfig: ThemesEnum.light | ThemesEnum.dark = ThemesEnum.light;

  @Input()
  searchData: ISearch;

  @Input()
  inputMinLength = 3;

  @Input()
  pushTemplate: TemplateRef<SdPushCardComponent>;

  @Input()
  openMobileSearch: Observable<void>;

  @ViewChild('searchInput', { static: false })
  searchInput?: SdFormInputComponent;

  @Output()
  inputSearch = new EventEmitter<string>();

  @Output()
  submitInputSearch = new EventEmitter<string>();

  @Output()
  clearInputSearch = new EventEmitter<void>();

  @Output()
  openSearch = new EventEmitter<string>();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.openedSearch ? 'is-opened-search' : '',
      this.themesConfig ? 'is-theme-' + this.themesConfig : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  protected readonly destroy$: Subject<void> = new Subject();
  private openMobileSearchSubscription: Subscription;

  documentClickSubscription: Subscription;
  inputSubscription: Subscription;
  mobileSubscription: Subscription;
  tabletSubscription: Subscription;
  isMobile$: boolean;
  isTablet$: boolean;
  openedSearch = false;
  isFilled = false;
  results = false;
  iconSize = IconSizeEnum;
  themesEnum = ThemesEnum;
  searchTerms: string;
  hasSuggestion: boolean;
  placeholder: string;

  constructor(
    private elRef: ElementRef,
    private app: ApplicationRef,
    private cdRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any,
    private deviceService: DeviceService,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const placeholder = this.translateService.instant('navigation.search.placeholder');
      if (placeholder?.includes('[')) {
        this.createRotatingPlaceholder(placeholder);
      } else {
        this.placeholder = placeholder;
      }

      this.hasSuggestion =
        this.searchData?.mostSearched?.items?.length > 0 ||
        this.pushTemplate.elementRef.nativeElement.childNodes.length > 0 ||
        this.searchData?.starProducts?.items?.length > 0;
    }

    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });

    this.tabletSubscription = this.deviceService.isTablet.subscribe((x) => {
      this.isTablet$ = x;
      this.cdRef.markForCheck();
    });

    this.openMobileSearchSubscription = this.openMobileSearch?.subscribe(() => {
      setTimeout(() => {
        this.toggleSearch();
      });
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.documentClickSubscription = fromEvent(document, 'click').subscribe(
        (event) => {
          const target = event.target as HTMLElement;
          if (
            this.openedSearch &&
            !this.elRef.nativeElement.contains(target) &&
            target.id !== 'searchDelete'
          ) {
            this.openedSearch = false;
          }
        },
      );

      this.inputSubscription = this.searchInput.inputChanged
        ?.pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((value) => {
          const trimmedValue = value.trim();
          this.searchTerms = value;
          if (trimmedValue.length >= this.inputMinLength) {
            this.isFilled = true;
            this.inputSearch.emit(trimmedValue);
          } else {
            this.isFilled = false;
          }
        });
    }
  }

  ngOnChanges() {
    this.results =
      this.searchData?.categories?.items?.length > 0 ||
      this.searchData?.products?.items?.length > 0 ||
      this.searchData?.sets?.items?.length > 0 ||
      this.searchData?.recipes?.items?.length > 0 ||
      this.searchData?.accessories?.items?.length > 0 ||
      this.searchData?.articles?.items?.length > 0 ||
      this.searchData?.others?.items?.length > 0;
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
    this.tabletSubscription?.unsubscribe();
    this.documentClickSubscription?.unsubscribe();
    this.inputSubscription?.unsubscribe();
    this.openMobileSearchSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSearch(event?: string) {
    this.openedSearch = !this.openedSearch;

    if (event || this.openedSearch) {
      this.openSearch.emit(event);
    }

    if (this.openedSearch) {
      this.focusOnSearchInput(300);
    }
  }

  openSearchResultBar(event?: string) {
    if (this.isSearchValueValid()) {
      this.openedSearch = true;
      this.openSearch.emit(event);
      this.focusOnSearchInput(300);
    }
  }

  inputIsActive() {
    setTimeout(()=>{
      this.openedSearch = true;
      this.openSearch.emit();
      this.focusOnSearchInput(300);
    }, 0);
  }

  onSubmit(event?: Event) {
    event?.preventDefault();
    if (this.isSearchValueValid()) {
      const searchValue = this.getSearchValue();
      this.submitInputSearch.emit(searchValue);
      this.searchInput.input.nativeElement.value = '';
      this.searchInput.input.nativeElement.blur();
      this.openedSearch = false;
    }
  }

  onClear() {
    this.clearInputSearch.emit();
    this.focusOnSearchInput(0);
  }

  onBlur() {
    if (!(this.isMobile$ || this.isTablet$)) {
      setTimeout(() => {
        this.openedSearch = false;
      }, 500);
    }
  }

  private isSearchValueValid(): boolean {
    return this.getSearchValue().length >= this.inputMinLength;
  }

  private getSearchValue(): string {
    return this.searchInput.input.nativeElement.value.trim();
  }

  private focusOnSearchInput(delay): void {
    setTimeout(() => this.searchInput.input.nativeElement.focus(), delay);
  }

  onopenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }

  createRotatingPlaceholder(placeholder: string) {
    const arrayPlaceholder = JSON.parse(placeholder).slice(0, 4);
    this.placeholder = arrayPlaceholder[0];

    this.app.isStable.pipe(
      filter((isStable) => isStable),
      take(1),
      switchMap(() => interval(2000)),
      takeUntil(this.destroy$)
    ).subscribe((x) => {
      this.updatePlaceholder(arrayPlaceholder, x);
    });
  }

  updatePlaceholder(arrayPlaceholder, i) {
    const j = (i + 1) % arrayPlaceholder.length;
    this.placeholder = arrayPlaceholder[j];
    this.cdRef.markForCheck();
  }

  getTotalSearchResults() {
    return Object.values(this.searchData).reduce(
      (total, { total: valueTotal = 0 }) => total + valueTotal,
      0,
    );
  }
}
